// Jungle Ruins — stage prop pack + placements (owner: the jungle stage; see layout.js for the folder contract).
//
// register(D, H): registers all 'jungle_*' prop builders on PropKit's definition table D.
// PLACEMENTS: set dressing for the Jungle Ruins stage (mirrored (x,z) -> (-x,-z) with rotY + PI unless mirror: false).

const P = Math.PI;

export function register(D, H) {
  const { THREE, col, shade, mixc, puffGeo, blobGeo, PI, TAU, HP } = H;
  const NS = (m) => (H.noShadow ? H.noShadow(m) : m);

  // ------------------------------------------------------------------------------------------ Jungle Ruins Palette
  const K = {
    stone: '#9a9884', stoneDk: '#686b5c', stoneLt: '#b8b49d', basalt: '#454a42',
    moss: '#3d6e38', mossDk: '#264a24', mossLt: '#5c8f49',
    jade: '#2e8a62', jadeLt: '#58b88a', jadeDk: '#1d5c40',
    gold: '#deb041', goldDk: '#a67d24', goldLt: '#f2cb68', bronze: '#7c6a43',
    cinnabar: '#a83c2b', terracotta: '#b85d3b',
    wood: '#6e4930', woodDk: '#472e1d', woodLt: '#946848',
    banyanBark: '#5e5246', banyanDk: '#3f362e', vine: '#38592f',
    canopyDk: '#244d29', canopy: '#366e38', canopyLt: '#56944b',
    palmBark: '#6b5642', fernLt: '#64a348', fernDk: '#2f5e29', flower: '#e0533c',
    canvas: '#c9bd9c', crateWood: '#8c6442', flame: '#ffaa38', flameCore: '#ffe28a',
    water: '#2b6e68',
  };

  const TPL = new Map();
  const tpl = (key, fn) => { let g = TPL.get(key); if (!g) { g = fn(); TPL.set(key, g); } return g; };
  const kf = (a) => (typeof a === 'number' ? a.toFixed(4) : String(a));
  const hash = (n) => { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s); };
  const pboxGeo = () => tpl('jr_pbox', () => new THREE.BoxGeometry(1, 1, 1));
  const pbox = (B, mat, c, w, h, d, x, y, z, o = {}) => B.add(mat, pboxGeo(), c, x, y, z, { ...o, sx: w, sy: h, sz: d });
  const cylGeo = (rt, rb, h, seg = 10, open = false) => tpl(['jr_cy', rt, rb, h, seg, open ? 1 : 0].map(kf).join('|'), () => new THREE.CylinderGeometry(rt, rb, h, seg, 1, open));
  const puff = (det, seed) => tpl('jr_pf|' + det + '|' + seed, () => puffGeo(det, seed));
  const blob = (r, det, seed) => tpl(['jr_bl', r, det, seed].map(kf).join('|'), () => blobGeo(r, det, seed));

  const ROOF = { roof: true };
  const RAIL = { rail: true };
  const colBox = (B, x, y, z, w, h, d, flag = undefined) => B.col(x - w / 2, y, z - d / 2, x + w / 2, y + h, z + d / 2, flag);

  function strut(B, mat, c, x0, y0, z0, x1, y1, z1, r = 0.04, o = {}) {
    const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0;
    const len = Math.hypot(dx, dy, dz);
    if (len < 1e-4) return;
    const mx = (x0 + x1) * 0.5, my = (y0 + y1) * 0.5, mz = (z0 + z1) * 0.5;
    const horiz = Math.hypot(dx, dz);
    if (horiz < 1e-4) {
      pbox(B, mat, c, r * 2, len, r * 2, mx, my, mz, o);
      return;
    }
    const ry = Math.atan2(dx, dz);
    const rx = Math.atan2(horiz, dy);
    B.add(mat, pboxGeo(), c, mx, my, mz, { ...o, sx: r * 2, sy: len, sz: r * 2, rx, ry });
  }

  function foliageCluster(B, cx, cy, cz, rx, ry, rz, count, rBase, cDk, cLt, seed) {
    const det = B.k?.qf >= 1 ? 2 : 1;
    for (let i = 0; i < count; i++) {
      const a = hash(seed + i * 3.1) * TAU;
      const u = hash(seed + i * 5.7) * 2 - 1;
      const rr = Math.pow(hash(seed + i * 7.3), 0.45);
      const sq = Math.sqrt(Math.max(0, 1 - u * u));
      const px = cx + Math.cos(a) * sq * rr * rx;
      const py = cy + u * rr * ry;
      const pz = cz + Math.sin(a) * sq * rr * rz;
      const sc = rBase * (0.72 + 0.45 * hash(seed + i * 11.1));
      const t = Math.max(0, Math.min(1, (u + 1) * 0.5 + 0.15 * (hash(seed + i * 13.7) - 0.5)));
      B.add('foliage', puff(det, (seed + i * 17) & 63), mixc(cDk, cLt, t), px, py, pz, {
        sx: sc * 1.18,
        sy: sc * 0.70,
        sz: sc * 1.14,
        ry: hash(seed + i * 19.3) * TAU,
      });
    }
  }

  const cx3 = (c) => { const k = col(c); return [k.r, k.g, k.b]; };
  const paintGeo = (g, c) => {
    const k = cx3(c), n = g.attributes.position.count, a = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) a.set(k, i * 3);
    g.setAttribute('color', new THREE.BufferAttribute(a, 3));
    return g;
  };
  const xform = (g, x, y, z, rx = 0, ry = 0, rz = 0, sx = 1, sy = sx, sz = sx) =>
    g.applyMatrix4(new THREE.Matrix4().compose(
      new THREE.Vector3(x, y, z),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz, 'YXZ')),
      new THREE.Vector3(sx, sy, sz)
    ));
  function mergeGeos(list) {
    let nv = 0, ni = 0;
    for (const g of list) { nv += g.attributes.position.count; ni += g.index ? g.index.count : g.attributes.position.count; }
    const pos = new Float32Array(nv * 3), nor = new Float32Array(nv * 3), uv = new Float32Array(nv * 2), cl = new Float32Array(nv * 3), idx = new Uint32Array(ni);
    let vo = 0, io = 0;
    for (const g of list) {
      const n = g.attributes.position.count;
      pos.set(g.attributes.position.array, vo * 3); nor.set(g.attributes.normal.array, vo * 3);
      if (g.attributes.uv) uv.set(g.attributes.uv.array, vo * 2);
      if (g.attributes.color) cl.set(g.attributes.color.array, vo * 3); else cl.fill(1, vo * 3, (vo + n) * 3);
      if (g.index) { const I = g.index.array; for (let k = 0; k < I.length; k++) idx[io++] = I[k] + vo; } else for (let k = 0; k < n; k++) idx[io++] = vo + k;
      vo += n;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3)); g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
    g.setAttribute('uv', new THREE.BufferAttribute(uv, 2)); g.setAttribute('color', new THREE.BufferAttribute(cl, 3));
    g.setIndex(new THREE.BufferAttribute(idx, 1));
    return g;
  }

  // ==========================================================================================================
  // 1. JUNGLE_SUNALTAR — Central Stepped Sun Monolith & Rotating Golden Astronomical Sun-Ring
  // ==========================================================================================================
  H.spinTemplate('jungle_sunring', () => {
    const Rw = 1.45, Ri = 0.58, rays = 12;
    const parts = [];
    // Outer carved golden calendar wheel segments
    for (let i = 0; i < rays; i++) {
      const a = (i / rays) * TAU;
      const ca = Math.cos(a), sa = Math.sin(a);
      parts.push(paintGeo(xform(new THREE.BoxGeometry(0.76, 0.16, 0.14), ca * Rw, 0, sa * Rw, 0, -(a + HP), 0), K.gold));
      parts.push(paintGeo(xform(new THREE.BoxGeometry(0.32, 0.14, 0.12), ca * Ri, 0, sa * Ri, 0, -(a + HP), 0), K.bronze));
      // Solar pointer spikes & jade cabochons
      const rSpike = Rw + 0.32;
      parts.push(paintGeo(xform(new THREE.BoxGeometry(0.18, 0.10, 0.52), ca * rSpike, 0, sa * rSpike, 0, -a, 0), i % 2 === 0 ? K.goldLt : K.cinnabar));
      if (i % 3 === 0) {
        parts.push(paintGeo(xform(new THREE.BoxGeometry(0.14, 0.18, Rw - Ri), ca * ((Rw + Ri) * 0.5), 0, sa * ((Rw + Ri) * 0.5), 0, -a, 0), K.jade));
      }
    }
    // Centraljade & gold solar core
    parts.push(paintGeo(xform(new THREE.CylinderGeometry(0.42, 0.42, 0.24, 12), 0, 0, 0), K.goldDk));
    parts.push(paintGeo(xform(new THREE.CylinderGeometry(0.24, 0.24, 0.30, 10), 0, 0, 0), K.jadeLt));
    return mergeGeos(parts);
  });

  D.jungle_sunaltar = {
    build(B) {
      // Stepped Mesoamerican stone monolith pedestal at the centre of the Sun Calendar Altar (y = 0.52)
      pbox(B, 'paint', K.stoneDk, 2.05, 0.55, 2.05, 0, 0.275, 0);
      pbox(B, 'paint', K.stone, 1.72, 0.95, 1.72, 0, 1.02, 0);
      pbox(B, 'paint', K.stoneLt, 1.38, 1.45, 1.38, 0, 2.22, 0);
      pbox(B, 'paint', K.basalt, 1.54, 0.24, 1.54, 0, 3.05, 0);

      // Carved jade & gold relief tablets on the 4 cardinal faces of the monolith
      for (const [dx, dz, ry] of [[0, 0.72, 0], [0, -0.72, PI], [0.72, 0, HP], [-0.72, 0, -HP]]) {
        pbox(B, 'metal', K.gold, 0.78, 0.92, 0.08, dx, 2.22, dz, { ry });
        pbox(B, 'paint', K.jade, 0.54, 0.68, 0.10, dx, 2.22, dz, { ry });
        pbox(B, NS('glow'), K.flameCore, 0.22, 0.22, 0.12, dx, 2.22, dz, { ry, glow: 1.4, ao: false });
      }

      // Moss cushions & corner bronze fire bowls around the pedestal
      for (const [sx, sz] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
        B.add('foliage', puff(1, (sx + sz + 5) & 15), K.moss, sx * 0.88, 0.58, sz * 0.88, { sx: 0.38, sy: 0.18, sz: 0.38 });
        B.add('metal', cylGeo(0.24, 0.16, 0.28, 8), K.bronze, sx * 0.82, 1.62, sz * 0.82);
        B.add(NS('glow'), puff(1, 7), K.flame, sx * 0.82, 1.82, sz * 0.82, { s: 0.16, glow: 1.85, ao: false });
      }

      // Twin stone horns / cradle pillars holding the spinning golden astronomical Sun-Ring overhead
      for (const sx of [-0.62, 0.62]) {
        pbox(B, 'paint', K.stone, 0.28, 1.55, 0.36, sx, 3.85, 0);
        pbox(B, 'metal', K.goldDk, 0.34, 0.22, 0.42, sx, 4.65, 0);
      }
      // Slowly rotating vertical golden Sun-Ring at y = 4.95 m
      B.spin('jungle_sunring', 0, 4.95, 0, { rx: HP, speed: 0.85 });

      // Solid cover collider around the monolith (leaves a wide 1.75 m walkable ring on the 5.6x5.6m altar slab)
      colBox(B, 0, 0, 0, 2.1, 4.8, 2.1, ROOF);
    },
  };

  // ==========================================================================================================
  // 2. JUNGLE_TEMPLE_FACADE — Upper Temple Sanctuary Stepped Pyramid Facade, Serpent Columns & Roof Comb
  // ==========================================================================================================
  D.jungle_temple_facade = {
    build(B, o = {}) {
      // Local origin sits at (0, 0, -45.4) facing +Z (toward the arena centre)
      const W = 18.0, yDeck = 2.8, yEave = 5.65;

      // 4 Carved Feathered-Serpent Square Columns along the front of the Sanctuary Terrace (z = +6.8 -> world z = -38.6)
      const colXs = [-8.4, -3.4, 3.4, 8.4];
      for (const cx of colXs) {
        pbox(B, 'paint', K.stoneDk, 0.62, 0.32, 0.62, cx, yDeck + 0.16, 6.8);
        pbox(B, 'paint', K.stone, 0.46, 2.55, 0.46, cx, yDeck + 1.44, 6.8);
        pbox(B, 'metal', K.gold, 0.54, 0.24, 0.54, cx, yDeck + 1.55, 6.8);
        pbox(B, 'paint', K.jade, 0.52, 0.18, 0.52, cx, yDeck + 2.25, 6.8);
        // Corbelled stone capital
        pbox(B, 'paint', K.stoneLt, 0.72, 0.28, 0.72, cx, yEave - 0.16, 6.8);
      }
      // Side sanctuary columns & carved stone serpent balustrades on outer edges (rails: false when stairs leave the
      // terrace's sides)
      for (const sx of o.rails === false ? [] : [-1, 1]) {
        pbox(B, 'paint', K.stone, 0.46, 2.75, 0.46, sx * 8.4, yDeck + 1.375, 3.4);
        pbox(B, 'paint', K.stoneDk, 0.32, 0.92, 6.4, sx * 8.82, yDeck + 0.46, 3.5);
        pbox(B, 'paint', K.stoneLt, 0.40, 0.16, 6.6, sx * 8.82, yDeck + 0.96, 3.5);
      }

      // Corbelled stone entablature lintel & stepped cornice over the sanctuary portico (z = 0..7.1)
      pbox(B, 'paint', K.stone, W + 0.8, 0.48, 6.8, 0, yEave + 0.24, 3.4);
      pbox(B, 'paint', K.stoneLt, W + 1.2, 0.32, 7.2, 0, yEave + 0.64, 3.4);
      pbox(B, 'metal', K.gold, W + 1.0, 0.14, 7.0, 0, yEave + 0.46, 3.4);
      // Central jade & gold Sun-God frieze tablet above the grand processional stair
      pbox(B, 'paint', K.basalt, 6.2, 0.92, 0.28, 0, yEave + 0.56, 7.02);
      pbox(B, 'metal', K.gold, 5.8, 0.72, 0.32, 0, yEave + 0.56, 7.04);
      pbox(B, 'paint', K.jadeLt, 1.6, 0.52, 0.36, 0, yEave + 0.56, 7.06);

      // Stepped pyramid superstructure rising behind the entablature (z = -3.6..1.2, y = 5.8..9.6)
      pbox(B, 'paint', K.stoneDk, W + 1.6, 1.4, 4.2, 0, 6.5, -1.2);
      pbox(B, 'paint', K.stone, W - 2.4, 1.3, 3.4, 0, 7.85, -1.2);
      pbox(B, 'paint', K.stoneLt, W - 6.4, 1.2, 2.6, 0, 9.1, -1.2);
      // Pierced Mayan Roof Comb (Crestería) crowning the pyramid ridge (y = 9.7..11.5)
      for (let rx = -4.5; rx <= 4.5; rx += 1.5) {
        pbox(B, 'paint', K.stoneLt, 0.75, 1.8, 0.65, rx, 10.6, -1.2);
      }
      pbox(B, 'paint', K.stone, 10.2, 0.38, 0.78, 0, 11.6, -1.2);
      B.add('metal', cylGeo(0.75, 0.75, 0.22, 12), K.gold, 0, 11.9, -1.2, { rx: HP });

      // Lush moss & hanging vines draping the temple cornice
      foliageCluster(B, -6.4, yEave + 0.95, 5.8, 1.6, 0.45, 1.1, 7, 0.45, K.mossDk, K.mossLt, 101);
      foliageCluster(B, 6.2, yEave + 0.95, 5.8, 1.6, 0.45, 1.1, 7, 0.45, K.mossDk, K.mossLt, 203);

      // Sanctuary portal doorway & glowing torch niches along the backwall (z = 0.12)
      pbox(B, 'paint', K.basalt, 2.6, 2.55, 0.24, 0, yDeck + 1.28, 0.12);
      pbox(B, 'paint', K.stoneLt, 3.1, 0.36, 0.32, 0, yDeck + 2.62, 0.16);
      for (const wx of [-5.8, -2.6, 2.6, 5.8]) {
        pbox(B, 'paint', K.basalt, 1.25, 1.65, 0.20, wx, yDeck + 1.40, 0.10);
        pbox(B, 'metal', K.bronze, 0.36, 0.28, 0.32, wx, yDeck + 1.35, 0.24);
        B.add(NS('glow'), puff(1, 5), K.flame, wx, yDeck + 1.58, 0.26, { s: 0.16, glow: 1.85, ao: false });
      }

      // Carved stone Cenote Water Basin on the flanking east sanctuary wing (x = 11.6, z = 2.6)
      pbox(B, 'paint', K.stoneDk, 2.2, 1.15, 2.2, 11.6, yDeck + 0.575, 2.6);
      pbox(B, 'paint', K.stoneLt, 2.36, 0.18, 2.36, 11.6, yDeck + 1.20, 2.6);
      pbox(B, 'paint', K.water, 1.88, 0.06, 1.88, 11.6, yDeck + 1.18, 2.6);
      pbox(B, 'paint', K.stone, 0.72, 1.15, 0.72, 11.6, yDeck + 1.72, 2.6);
      colBox(B, 11.6, yDeck, 2.6, 2.3, 2.4, 2.3, ROOF);

      // Off-limits roof collider over the sanctuary portico entablature
      colBox(B, 0, yEave - 0.1, 3.4, W + 0.8, 3.2, 7.0, ROOF);
    },
  };

  // ==========================================================================================================
  // 3. JUNGLE_CLOISTER_RUIN — Overgrown Cloister Vaulting, Corbelled Arches & Relic Hoist
  // ==========================================================================================================
  D.jungle_cloister_ruin = {
    build(B) {
      // Local origin at (-20.7, 0, -20.5): dresses the western outer wall of the Cloister Roof (y = 2.6)
      // Sits at local x = -5.3..-2.7 (world x = -26.0..-23.4), keeping the 8x11 m Cloister Roof zone open
      const yLoft = 2.6, hVault = 2.5;
      pbox(B, 'paint', K.stone, 2.5, hVault, 10.6, -4.0, yLoft + hVault * 0.5, 0);
      pbox(B, 'paint', K.stoneLt, 2.8, 0.34, 11.0, -4.0, yLoft + hVault + 0.17, 0);
      // Pierced stone roof-comb blocks along the top ridge
      for (const rz of [-4.2, -1.4, 1.4, 4.2]) {
        pbox(B, 'paint', K.stoneDk, 0.65, 0.95, 1.45, -4.0, yLoft + hVault + 0.80, rz);
      }
      // Three recessed Mayan corbelled arch niches with glowing jade/torch shrines along the east face
      for (const vz of [-3.4, 0, 3.4]) {
        pbox(B, 'paint', K.basalt, 0.22, 1.65, 1.85, -2.72, yLoft + 1.05, vz);
        pbox(B, 'metal', K.gold, 0.26, 0.22, 2.05, -2.70, yLoft + 1.95, vz);
        B.add(NS('glow'), puff(1, 3), K.flame, -2.62, yLoft + 1.15, vz, { s: 0.13, glow: 1.5, ao: false });
      }
      // Lush moss & fern cushions overgrowing the cloister vault roof
      foliageCluster(B, -3.8, yLoft + hVault + 0.55, -2.5, 1.2, 0.5, 2.2, 8, 0.48, K.mossDk, K.mossLt, 307);
      foliageCluster(B, -3.8, yLoft + hVault + 0.55, 2.8, 1.2, 0.5, 2.2, 8, 0.48, K.mossDk, K.mossLt, 409);

      // Projecting tropical hardwood excavation crane beam & hanging golden relic cage (high above head height)
      pbox(B, 'wood', K.woodDk, 4.5, 0.24, 0.24, -1.2, yLoft + 2.38, 3.8);
      strut(B, 'wood', K.wood, -2.7, yLoft + 1.55, 3.8, -0.2, yLoft + 2.38, 3.8, 0.055);
      B.add('metal', cylGeo(0.03, 0.03, 0.52, 6), K.bronze, 0.82, yLoft + 2.02, 3.8);
      B.add('metal', cylGeo(0.18, 0.14, 0.22, 8), K.gold, 0.82, yLoft + 1.72, 3.8);

      colBox(B, -4.0, yLoft, 0, 2.6, hVault + 1.1, 10.8, ROOF);
    },
  };

  // ==========================================================================================================
  // 4. JUNGLE_IDOL — Colossal Carved Stone Guardian Head & Stepped Altar Plinth (solid cover)
  // ==========================================================================================================
  D.jungle_idol = {
    build(B) {
      // Stepped stone pedestal base
      pbox(B, 'paint', K.stoneDk, 2.05, 0.42, 1.85, 0, 0.21, 0);
      pbox(B, 'paint', K.stone, 1.72, 1.48, 1.52, 0, 1.16, 0);
      // Carved feathered headdress crown & brow
      pbox(B, 'paint', K.stoneLt, 1.92, 0.42, 1.68, 0, 2.05, 0.06);
      for (let i = -2; i <= 2; i++) {
        pbox(B, 'paint', i % 2 === 0 ? K.jade : K.gold, 0.28, 0.42, 0.24, i * 0.34, 2.42, 0.08);
      }
      // Jade ear spools on ±X sides
      for (const sx of [-0.96, 0.96]) {
        B.add('paint', cylGeo(0.32, 0.32, 0.18, 10), K.jade, sx, 1.25, 0, { rz: HP });
        B.add('metal', cylGeo(0.16, 0.16, 0.22, 8), K.gold, sx, 1.25, 0, { rz: HP });
      }
      // Sculpted nose bridge, fanged mouth & glowing jade eyes on +Z face
      pbox(B, 'paint', K.stoneLt, 0.42, 0.56, 0.26, 0, 1.32, 0.82);
      pbox(B, 'paint', K.basalt, 0.88, 0.34, 0.18, 0, 0.78, 0.78);
      pbox(B, 'metal', K.gold, 0.96, 0.10, 0.22, 0, 0.96, 0.80);
      for (const sx of [-0.42, 0.42]) {
        pbox(B, 'paint', K.basalt, 0.32, 0.24, 0.18, sx, 1.48, 0.78);
        pbox(B, NS('glow'), K.jadeLt, 0.22, 0.14, 0.20, sx, 1.48, 0.80, { glow: 1.5, ao: false });
      }
      // Moss cap on top of the idol
      foliageCluster(B, 0, 2.35, -0.1, 0.85, 0.28, 0.72, 5, 0.36, K.mossDk, K.mossLt, 73);

      colBox(B, 0, 0, 0, 2.05, 2.45, 1.85, ROOF);
    },
  };

  // ==========================================================================================================
  // 5. JUNGLE_PILLAR_BROKEN — Fallen Fluted Temple Column Drums & Mossy Lintel Blocks
  // ==========================================================================================================
  D.jungle_pillar_broken = {
    build(B, o = {}) {
      const s = o.s || 1.0, kind = o.kind || 0;
      if (kind === 1) {
        // Rectangular carved stone lintel & fallen drum
        pbox(B, 'paint', K.stoneDk, 2.5 * s, 0.86 * s, 0.95 * s, 0, 0.43 * s, 0);
        pbox(B, 'paint', K.stoneLt, 2.6 * s, 0.14 * s, 1.02 * s, 0, 0.88 * s, 0);
        pbox(B, 'paint', K.jade, 1.8 * s, 0.28 * s, 0.98 * s, 0, 0.48 * s, 0);
        foliageCluster(B, 0, 0.96 * s, 0, 0.95 * s, 0.24 * s, 0.42 * s, 5, 0.28 * s, K.mossDk, K.mossLt, 91);
        if (o.solid !== false) colBox(B, 0, 0, 0, 2.6 * s, 0.96 * s, 1.02 * s);
      } else {
        // Stepped base + broken fluted column drum + fallen drum beside it
        pbox(B, 'paint', K.stoneDk, 1.55 * s, 0.36 * s, 1.55 * s, -0.35 * s, 0.18 * s, 0);
        B.add('paint', cylGeo(0.58 * s, 0.64 * s, 1.45 * s, 12), K.stone, -0.35 * s, 0.36 * s + 0.725 * s, 0);
        B.add('metal', cylGeo(0.66 * s, 0.66 * s, 0.16 * s, 12), K.goldDk, -0.35 * s, 1.15 * s, 0);
        B.add('paint', cylGeo(0.54 * s, 0.54 * s, 1.15 * s, 12), K.stoneLt, 0.58 * s, 0.54 * s, 0.12 * s, { rz: HP, ry: 0.28 });
        foliageCluster(B, -0.35 * s, 1.85 * s, 0, 0.55 * s, 0.25 * s, 0.55 * s, 5, 0.28 * s, K.mossDk, K.mossLt, 57);
        if (o.solid !== false) colBox(B, 0, 0, 0, 2.35 * s, 1.85 * s, 1.55 * s, ROOF);
      }
    },
  };

  // ==========================================================================================================
  // 6. JUNGLE_BALUSTRADE — Low Carved Stone Serpent Wall Railing (rail: true collider)
  // ==========================================================================================================
  D.jungle_balustrade = {
    build(B, o = {}) {
      const len = o.len || 4.4, h = o.h || 1.06;
      // Continuous carved stone low wall with terminal serpent-head piers
      pbox(B, 'paint', K.stoneDk, len, 0.28, 0.34, 0, 0.14, 0);
      pbox(B, 'paint', K.stone, len - 0.3, h - 0.42, 0.26, 0, 0.28 + (h - 0.42) * 0.5, 0);
      pbox(B, 'paint', K.stoneLt, len + 0.08, 0.16, 0.38, 0, h - 0.08, 0);
      pbox(B, 'metal', K.goldDk, len - 0.5, 0.12, 0.30, 0, h * 0.56, 0);
      for (const sx of [-1, 1]) {
        const px = sx * (len * 0.5 - 0.18);
        pbox(B, 'paint', K.stoneLt, 0.44, h + 0.08, 0.44, px, (h + 0.08) * 0.5, 0);
        pbox(B, 'paint', K.jade, 0.26, 0.26, 0.48, px, h * 0.65, 0);
      }
      if (o.solid !== false) colBox(B, 0, 0, 0, len + 0.16, h, 0.42, RAIL);
    },
  };

  // ==========================================================================================================
  // 7. JUNGLE_CRATES — Archaeological Expedition Relic Crates, Canvas Tarp & Brass Lantern
  // ==========================================================================================================
  D.jungle_crates = {
    build(B, o = {}) {
      const stack = o.stack || 2;
      // Wooden expedition crates reinforced with bronze straps
      pbox(B, 'wood', K.crateWood, 1.08, 1.04, 1.04, -0.56, 0.52, 0, { ry: 0.05 });
      pbox(B, 'wood', K.woodDk, 1.12, 0.08, 1.08, -0.56, 0.98, 0, { ry: 0.05 });
      pbox(B, 'paint', K.canvas, 1.06, 1.06, 1.02, 0.56, 0.53, 0.04, { ry: -0.06 });
      pbox(B, NS('metal'), K.bronze, 1.08, 0.05, 1.04, 0.56, 0.78, 0.04, { ry: -0.06 });
      if (stack >= 3) {
        pbox(B, 'wood', K.woodLt, 0.96, 0.88, 0.92, -0.05, 1.48, -0.02, { ry: 0.12 });
      }
      // Warm expedition brass lantern sitting on top of the crate
      const ly = stack >= 3 ? 1.92 : 1.06;
      B.add('metal', cylGeo(0.12, 0.14, 0.28, 8), K.gold, 0.42, ly + 0.14, 0.12);
      B.add(NS('glow'), puff(1, 2), K.flameCore, 0.42, ly + 0.15, 0.12, { s: 0.09, glow: 1.4, ao: false });

      colBox(B, 0, 0, 0, 2.26, stack >= 3 ? 1.95 : 1.10, 1.12, stack >= 3 ? ROOF : undefined);
    },
  };

  // ==========================================================================================================
  // 8. JUNGLE_BANYAN — Giant Buttress-Rooted Tropical Banyan / Ceiba Rainforest Tree
  // ==========================================================================================================
  D.jungle_banyan = {
    build(B, o = {}) {
      const s = o.s || 1.0, seed = o.seed || 1;
      const hTrunk = 3.5 * s;
      // Thick fluted trunk + 4 flared buttress roots gripping the temple stone
      B.add('wood', cylGeo(0.38 * s, 0.62 * s, hTrunk, 9), K.banyanBark, 0, hTrunk * 0.5, 0);
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * TAU + 0.35;
        strut(B, 'wood', K.banyanDk, Math.cos(a) * 0.95 * s, 0.08 * s, Math.sin(a) * 0.95 * s, Math.cos(a) * 0.28 * s, 1.65 * s, Math.sin(a) * 0.28 * s, 0.16 * s);
      }
      // 3 spreading rainforest canopy limbs + hanging liana vines
      const limbs = [
        [1.45 * s, (hTrunk + 1.45 * s), 0.65 * s, 1.85 * s, 0.95 * s, 1.75 * s],
        [-1.35 * s, (hTrunk + 1.25 * s), -0.75 * s, 1.75 * s, 0.92 * s, 1.65 * s],
        [0.15 * s, (hTrunk + 2.15 * s), -0.25 * s, 1.95 * s, 1.05 * s, 1.85 * s],
      ];
      limbs.forEach(([lx, ly, lz, rx, ry, rz], idx) => {
        strut(B, 'wood', K.banyanBark, 0, hTrunk - 0.35 * s, 0, lx * 0.78, ly - 0.3 * s, lz * 0.78, 0.16 * s);
        foliageCluster(B, lx, ly, lz, rx, ry, rz, 11, 0.68 * s, K.canopyDk, K.canopyLt, seed * 31 + idx * 19);
        // Hanging liana vine from limb
        B.add('foliage', cylGeo(0.03 * s, 0.025 * s, 1.65 * s, 5), K.vine, lx * 0.65, ly - 1.15 * s, lz * 0.65);
      });

      if (o.solid !== false) colBox(B, 0, 0, 0, 0.95 * s, 3.4 * s, 0.95 * s, ROOF);
    },
  };

  // ==========================================================================================================
  // 9. JUNGLE_PALM & JUNGLE_FERN — Tropical Fan Palms, Giant Monstera Ferns & Rainforest Flora
  // ==========================================================================================================
  D.jungle_palm = {
    build(B, o = {}) {
      const s = o.s || 1.0, h = (o.h || 2.8) * s, seed = o.seed || 7;
      // Ringed tropical palm trunk with slight graceful curve
      B.add('wood', cylGeo(0.16 * s, 0.24 * s, h, 8), K.palmBark, 0, h * 0.5, 0, { rx: 0.05 });
      // Crown of spreading tropical palm fronds
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * TAU + (seed & 7) * 0.2;
        const fx = Math.cos(a) * 0.85 * s, fz = Math.sin(a) * 0.85 * s;
        strut(B, 'foliage', i % 2 ? K.fernLt : K.canopy, 0, h + 0.12 * s, 0, fx, h - 0.18 * s, fz, 0.14 * s);
      }
      foliageCluster(B, 0, h + 0.18 * s, 0, 0.95 * s, 0.42 * s, 0.95 * s, 8, 0.38 * s, K.canopyDk, K.fernLt, seed * 17);
      if (o.solid !== false) colBox(B, 0, 0, 0, 0.52 * s, h + 0.35 * s, 0.52 * s, ROOF);
    },
  };

  D.jungle_fern = {
    build(B, o = {}) {
      const s = o.s || 1.0, seed = o.seed || 5;
      foliageCluster(B, 0, 0.28 * s, 0, 0.88 * s, 0.38 * s, 0.88 * s, 8, 0.34 * s, K.fernDk, K.fernLt, seed * 23);
      // Bright scarlet bromeliad flower spike in the centre of select fern clusters
      if ((seed & 1) === 1) {
        B.add('foliage', cylGeo(0.05 * s, 0.08 * s, 0.32 * s, 6), K.flower, 0, 0.48 * s, 0);
      }
    },
  };

  // ==========================================================================================================
  // 10. JUNGLE_SIGNPOST & JUNGLE_BRAZIER — Expedition Tablet Signpost & Carved Stone Tripod Fire-Brazier
  // ==========================================================================================================
  D.jungle_signpost = {
    build(B, o = {}) {
      B.add('wood', cylGeo(0.07, 0.09, 2.65, 8), K.woodDk, 0, 1.325, 0);
      // Carved stone solar directional tablet + timber expedition fingerboards
      pbox(B, 'paint', K.stone, 0.72, 0.72, 0.10, 0, 2.15, 0.06);
      pbox(B, 'metal', K.gold, 0.56, 0.56, 0.12, 0, 2.15, 0.06);
      pbox(B, 'paint', K.jade, 0.32, 0.32, 0.14, 0, 2.15, 0.06);
      pbox(B, 'wood', K.woodLt, 1.05, 0.20, 0.06, 0.30, 2.68, 0, { ry: 0.2 });
      pbox(B, 'wood', K.wood, 0.95, 0.20, 0.06, -0.28, 2.90, 0, { ry: -0.35 });
      if (o.solid !== false) colBox(B, 0, 0, 0, 0.28, 2.75, 0.28, ROOF);
    },
  };

  D.jungle_brazier = {
    build(B, o = {}) {
      // Carved stone obelisk torch pillar + bronze cantilever arm reaching +0.92 m toward arena centre (matching lampPools)
      pbox(B, 'paint', K.stoneDk, 0.44, 0.42, 0.44, 0, 0.21, 0);
      pbox(B, 'paint', K.stone, 0.28, 4.45, 0.28, 0, 2.42, 0);
      pbox(B, 'metal', K.goldDk, 0.34, 0.22, 0.34, 0, 3.85, 0);
      // Cantilevered bronze brazier arm toward +X in local frame (orient via rotY so +X points toward X=0)
      pbox(B, 'metal', K.bronze, 0.96, 0.08, 0.08, 0.46, 4.78, 0);
      strut(B, 'metal', K.bronze, 0.10, 4.28, 0, 0.65, 4.78, 0, 0.035);
      // Hanging bronze fire-basket + glowing dusk flame at (0.92, 4.65, 0)
      B.add('metal', cylGeo(0.28, 0.14, 0.24, 10), K.bronze, 0.92, 4.62, 0);
      B.add('metal', cylGeo(0.32, 0.30, 0.06, 10), K.gold, 0.92, 4.74, 0);
      B.add(NS('glow'), puff(1, 5), K.flame, 0.92, 4.82, 0, { s: 0.18, glow: 1.95, ao: false });
      B.add(NS('glow'), puff(1, 2), K.flameCore, 0.92, 4.88, 0, { s: 0.11, glow: 2.2, ao: false });
      if (o.solid !== false) colBox(B, 0, 0, 0, 0.42, 4.85, 0.42, ROOF);
    },
  };
}

// ==========================================================================================================
// PLACEMENTS — Set dressing for Jungle Ruins (Alpha half; mirrored automatically for Bravo unless mirror: false)
// ==========================================================================================================
export const PLACEMENTS = [
  // Sun Monolith & rotating golden Sun-Ring on the summit of the Great Ziggurat (single, not mirrored)
  { type: 'jungle_sunaltar', pos: [0, 2.7, 0], rotY: 0, mirror: false },

  // Sanctuary facade over each team's off-centre spawn terrace (side stairs leave the terrace: no side balustrades)
  { type: 'jungle_temple_facade', pos: [4, 0, -45.4], rotY: 0, rails: false },

  // Corbelled vault ruin & relic hoist along the outer edge of the rear treehouse deck
  { type: 'jungle_cloister_ruin', pos: [-20.7, 0, -34.0], rotY: 0 },

  // Guardian idols: Shrine Plaza, and one by each banyan mound on the forest floor
  { type: 'jungle_idol', pos: [7.2, 0, -21.6], rotY: -0.4 },
  { type: 'jungle_idol', pos: [12.6, 0, 6.4], rotY: -1.9 },

  // Fallen column drums & lintels (plaza cover, the cenote rim, the forest floor)
  { type: 'jungle_pillar_broken', pos: [-8.2, 0, -28.4], rotY: 0.4, kind: 0 },
  { type: 'jungle_pillar_broken', pos: [1.6, 0, -17.6], rotY: 0.1, kind: 1 },
  { type: 'jungle_pillar_broken', pos: [-12.6, 0, -22.4], rotY: 1.3, kind: 1 },
  { type: 'jungle_pillar_broken', pos: [9.6, 0, -35.6], rotY: -0.3, kind: 0, s: 0.95 },
  { type: 'jungle_pillar_broken', pos: [10.8, 0, -7.4], rotY: 0.7, kind: 0, s: 0.9 },
  { type: 'jungle_pillar_broken', pos: [-23.6, 0, -24.2], rotY: 0.2, kind: 1, s: 0.9 },

  // Serpent balustrades on the cenote rim and the canopy decks (rail: kids blocked, shots & squids pass)
  { type: 'jungle_balustrade', pos: [15.6, 0, -15.6], rotY: 0, len: 4.4 },
  { type: 'jungle_balustrade', pos: [23.2, 0, -30.4], rotY: 0, len: 3.4 },
  { type: 'jungle_balustrade', pos: [-24.3, 2.6, -12.25], rotY: 0, len: 2.6 },

  // Expedition crates (canopy decks, plaza, cenote floor)
  { type: 'jungle_crates', pos: [-19.4, 2.6, -36.8], rotY: 0.1, stack: 2 },
  { type: 'jungle_crates', pos: [-24.2, 2.6, -17.6], rotY: P * 0.5, stack: 2, notIn: 'zones' },
  { type: 'jungle_crates', pos: [-6.6, 0, -34.6], rotY: 0.35, stack: 3 },
  { type: 'jungle_crates', pos: [8.4, 0, -14.8], rotY: -0.25, stack: 2 },
  { type: 'jungle_crates', pos: [24.6, -1.2, -22.2], rotY: P * 0.5, stack: 2 },

  // Giant banyans: on the root mounds, holding up the canopy boardwalk, and at the plaza corners
  { type: 'jungle_banyan', pos: [18.0, 0.7, 2.4], rotY: 0.6, s: 1.2, seed: 11 },
  { type: 'jungle_banyan', pos: [-19.0, 0, -24.4], rotY: 2.1, s: 1.15, seed: 23 },
  { type: 'jungle_banyan', pos: [-24.6, 0, -41.0], rotY: 1.4, s: 1.05, seed: 37 },
  { type: 'jungle_banyan', pos: [24.6, 0, -13.4], rotY: -0.9, s: 0.95, seed: 51 },

  // Palms round the cenote and spawn, ferns everywhere the stone lets them
  { type: 'jungle_palm', pos: [24.6, 0, -33.6], rotY: 0.4, s: 1.1, seed: 3 },
  { type: 'jungle_palm', pos: [12.8, 0, -12.6], rotY: 1.8, s: 1.0, seed: 9 },
  { type: 'jungle_palm', pos: [-12.6, 0, -42.4], rotY: -0.7, s: 0.95, seed: 15 },
  { type: 'jungle_palm', pos: [23.4, 0, 8.4], rotY: 2.6, s: 1.05, seed: 21 },
  { type: 'jungle_fern', pos: [21.2, -1.2, -28.4], rotY: 0.2, s: 1.15, seed: 5 },
  { type: 'jungle_fern', pos: [13.4, -1.2, -17.2], rotY: 1.5, s: 1.1, seed: 12 },
  { type: 'jungle_fern', pos: [17.0, -1.2, -28.8], rotY: 2.4, s: 1.05, seed: 19 },
  { type: 'jungle_fern', pos: [-7.4, 0, -20.6], rotY: 0.8, s: 1.1, seed: 7 },
  { type: 'jungle_fern', pos: [6.6, 0.9, -7.0], rotY: -1.1, s: 0.9, seed: 17 },
  { type: 'jungle_fern', pos: [-15.6, 0, -6.6], rotY: 0.3, s: 1.15, seed: 29 },
  { type: 'jungle_fern', pos: [-25.0, 0, -31.0], rotY: 1.9, s: 1.0, seed: 41 },
  { type: 'jungle_fern', pos: [16.0, 0.7, 4.4], rotY: 1.2, s: 1.0, seed: 33 },

  // Expedition signpost at the gateway
  { type: 'jungle_signpost', pos: [6.0, 0, -15.2], rotY: -0.25 },

  // Fire-braziers (matching LAYOUT.decor.lampPools so dusk light pools line up with the flames)
  { type: 'jungle_brazier', pos: [-2.2, 0, -32.4], rotY: 0 },
  { type: 'jungle_brazier', pos: [10.2, 0, -32.4], rotY: P },
  { type: 'jungle_brazier', pos: [-6.4, 0, -12.8], rotY: 0 },
  { type: 'jungle_brazier', pos: [6.4, 0, -12.8], rotY: P },
];
